import type { Chart, Plugin } from "chart.js";

const MIN_DRAG_PX = 4;
const FILL_COLOR = "rgba(242, 122, 97, 0.15)";
const STROKE_COLOR = "rgba(242, 122, 97, 0.6)";

type SelectionPayload = {
  startMs: number;
  endMs: number;
  pixelMidX: number;
  pixelTopY: number;
};

export type RangeSelectOptions = {
  onChange: (sel: SelectionPayload) => void;
  onEnd: () => void;
};

type PluginState = {
  dragStartPx: number | null;
  dragCurrentPx: number | null;
  rafPending: boolean;
  prevTooltipEnabled: boolean | undefined;
  onMouseDown: (e: MouseEvent) => void;
  onMouseMove: (e: MouseEvent) => void;
  onMouseUp: (e: MouseEvent) => void;
  onWindowMouseUp: (e: MouseEvent) => void;
  onWindowKeyDown: (e: KeyboardEvent) => void;
  onResize: () => void;
};

const STATE = new WeakMap<Chart, PluginState>();

// chart.js plugin options are typed only for built-in plugins, so we cast
// through a narrow shape rather than augmenting the whole module.
const getOpts = (chart: Chart): RangeSelectOptions | undefined =>
  (chart.options.plugins as { rangeSelect?: RangeSelectOptions } | undefined)
    ?.rangeSelect;

function endDrag(chart: Chart, opts: RangeSelectOptions | undefined) {
  const s = STATE.get(chart);
  if (!s) return;
  const wasDragging = s.dragStartPx !== null;
  s.dragStartPx = null;
  s.dragCurrentPx = null;
  if (wasDragging) {
    const tooltip = chart.options.plugins?.tooltip;
    if (tooltip) tooltip.enabled = s.prevTooltipEnabled;
    window.removeEventListener("mouseup", s.onWindowMouseUp);
    window.removeEventListener("keydown", s.onWindowKeyDown);
    opts?.onEnd();
    chart.draw();
  }
}

function emitChange(chart: Chart, opts: RangeSelectOptions) {
  const s = STATE.get(chart);
  if (!s || s.dragStartPx === null || s.dragCurrentPx === null) return;
  const { left, right } = chart.chartArea;
  const a = Math.min(s.dragStartPx, s.dragCurrentPx);
  const b = Math.max(s.dragStartPx, s.dragCurrentPx);
  const clampedA = Math.max(left, Math.min(right, a));
  const clampedB = Math.max(left, Math.min(right, b));
  const startMs = chart.scales.x.getValueForPixel(clampedA);
  const endMs = chart.scales.x.getValueForPixel(clampedB);
  if (startMs == null || endMs == null) return;
  opts.onChange({
    startMs,
    endMs,
    pixelMidX: (clampedA + clampedB) / 2,
    pixelTopY: chart.chartArea.top,
  });
}

export const rangeSelectPlugin: Plugin = {
  id: "rangeSelect",

  afterInit(chart: Chart) {
    const canvas = chart.canvas;

    const getCanvasPx = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x:
          ((e.clientX - rect.left) * canvas.width) /
          rect.width /
          (window.devicePixelRatio || 1),
        y:
          ((e.clientY - rect.top) * canvas.height) /
          rect.height /
          (window.devicePixelRatio || 1),
      };
    };

    const insideChartArea = (x: number, y: number) => {
      const { left, right, top, bottom } = chart.chartArea;
      return x >= left && x <= right && y >= top && y <= bottom;
    };

    const state: PluginState = {
      dragStartPx: null,
      dragCurrentPx: null,
      rafPending: false,
      prevTooltipEnabled: undefined,

      onMouseDown(e: MouseEvent) {
        if (e.button !== 0) return;
        const opts = getOpts(chart);
        if (!opts) return;
        const { x, y } = getCanvasPx(e);
        if (!insideChartArea(x, y)) return;
        state.dragStartPx = x;
        state.dragCurrentPx = x;
        const tooltip = chart.options.plugins?.tooltip;
        if (tooltip) {
          state.prevTooltipEnabled = tooltip.enabled as boolean | undefined;
          tooltip.enabled = false;
        }
        window.addEventListener("mouseup", state.onWindowMouseUp);
        window.addEventListener("keydown", state.onWindowKeyDown);
        e.preventDefault();
      },

      onMouseMove(e: MouseEvent) {
        if (state.dragStartPx === null) return;
        const { x } = getCanvasPx(e);
        state.dragCurrentPx = x;
        if (state.rafPending) return;
        state.rafPending = true;
        requestAnimationFrame(() => {
          state.rafPending = false;
          if (state.dragStartPx === null) return;
          chart.draw();
          if (
            Math.abs((state.dragCurrentPx ?? 0) - state.dragStartPx) <
            MIN_DRAG_PX
          )
            return;
          const opts = getOpts(chart);
          if (opts) emitChange(chart, opts);
        });
      },

      onMouseUp() {
        const opts = getOpts(chart);
        endDrag(chart, opts);
      },

      onWindowMouseUp() {
        const opts = getOpts(chart);
        endDrag(chart, opts);
      },

      onWindowKeyDown(e: KeyboardEvent) {
        if (e.key !== "Escape") return;
        const opts = getOpts(chart);
        endDrag(chart, opts);
      },

      onResize() {
        const opts = getOpts(chart);
        endDrag(chart, opts);
      },
    };

    canvas.addEventListener("mousedown", state.onMouseDown);
    canvas.addEventListener("mousemove", state.onMouseMove);
    canvas.addEventListener("mouseup", state.onMouseUp);
    STATE.set(chart, state);
  },

  afterDatasetsDraw(chart: Chart) {
    const s = STATE.get(chart);
    if (!s || s.dragStartPx === null || s.dragCurrentPx === null) return;
    if (Math.abs(s.dragCurrentPx - s.dragStartPx) < MIN_DRAG_PX) return;
    const { left, right, top, bottom } = chart.chartArea;
    const a = Math.max(
      left,
      Math.min(right, Math.min(s.dragStartPx, s.dragCurrentPx)),
    );
    const b = Math.max(
      left,
      Math.min(right, Math.max(s.dragStartPx, s.dragCurrentPx)),
    );
    const { ctx } = chart;
    ctx.save();
    ctx.fillStyle = FILL_COLOR;
    ctx.fillRect(a, top, b - a, bottom - top);
    ctx.strokeStyle = STROKE_COLOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(a + 0.5, top);
    ctx.lineTo(a + 0.5, bottom);
    ctx.moveTo(b - 0.5, top);
    ctx.lineTo(b - 0.5, bottom);
    ctx.stroke();
    ctx.restore();
  },

  resize(chart: Chart) {
    const s = STATE.get(chart);
    s?.onResize();
  },

  beforeDestroy(chart: Chart) {
    const s = STATE.get(chart);
    if (!s) return;
    chart.canvas.removeEventListener("mousedown", s.onMouseDown);
    chart.canvas.removeEventListener("mousemove", s.onMouseMove);
    chart.canvas.removeEventListener("mouseup", s.onMouseUp);
    window.removeEventListener("mouseup", s.onWindowMouseUp);
    window.removeEventListener("keydown", s.onWindowKeyDown);
    STATE.delete(chart);
  },
};
